import { useEffect, useEffectEvent } from "react";

import type { EligibleProduct } from "../../../types";

import { getProductOffer } from "../../../api/getProductOffer";
import { handleEnrollmentEvent } from "../../../handlers/handleEnrollmentEvent";
import { useSalesContext } from "../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../stores";

/**
 * When the expert selects a product, fetch its current enrollment status once
 * and update the store. Mirrors the host-side useQuerySelectedProductOffer.
 */
export const useQuerySelectedProductOffer = () => {
    const { sessionId, partner, logger, analytics, apiClients, isSimulatedVoiceTask } = useSalesContext();
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));

    const onUpdate = useEffectEvent(async (product: EligibleProduct) => {
        try {
            // In training mode, we don't want to call the product-offer API since its response does not matter for training
            if (isSimulatedVoiceTask && product.salesProvider === "expert-university") {
                return;
            }

            const { enrollmentStatus } = await getProductOffer(
                apiClients.sales,
                logger,
                analytics.dispatcher,
                product.id,
                partner,
                product.salesProvider,
                sessionId,
            );

            if (enrollmentStatus == null) return;

            handleEnrollmentEvent(analytics, {
                offerId: product.id,
                status: enrollmentStatus,
                salesProvider: product.salesProvider,
            });
        } catch (error) {
            void analytics.dispatcher.dispatchBusinessEvent("GetProductOfferError", {
                productOfferId: product.id,
                partner,
                error,
            });
        }
    });

    useEffect(() => {
        if (selectedProduct == null) return;
        void onUpdate(selectedProduct);
    }, [partner, selectedProduct]);
};
